import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Phone } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import BrandLogo from '@/components/brand/BrandLogo';
import { BRAND } from '@/theme/brand';

const DEFAULT_NAV = [
  { label: 'Home', to: '/' },
  { label: 'Book Cab', to: '/book-vehicle', bookCab: true },
  { label: 'Outstation', to: '/book-vehicle?trip=round_trip', outstation: true },
];

const HOME_NAV = [
  { label: 'Home', to: '/', hash: null },
  { label: 'Book a Cab', to: '/book-vehicle', hash: '#booking' },
  { label: 'Our Fleet', to: '/', hash: '#fleet' },
  { label: 'Popular Routes', to: '/', hash: '#routes' },
  { label: 'Contact', to: '/', hash: '#contact' },
];

const SiteHeader = ({
  showNav = true,
  showAdmin = false,
  backTo,
  backLabel = 'Back',
  homepage = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [scrolled, setScrolled] = React.useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const trip = searchParams.get('trip');

  const isActive = (item) => {
    if (item.hash) {
      return location.pathname === '/' && location.hash === item.hash;
    }
    if (item.to === '/') return location.pathname === '/' && !location.hash;
    if (item.outstation) {
      return location.pathname === '/book-vehicle' && trip === 'round_trip';
    }
    if (item.bookCab) {
      return location.pathname === '/book-vehicle' && trip !== 'round_trip';
    }
    return location.pathname.startsWith(item.to);
  };

  const navClass = (item) => {
    const active = isActive(item);
    const base = homepage
      ? 'text-[14px] font-medium text-[#1B2D5E] font-body transition-colors relative pb-0.5'
      : 'nav-link transition-colors relative pb-1';
    return `${base} ${active ? 'nav-link-active' : homepage ? 'hover:text-[#E8651A]' : 'text-[#555555] hover:text-atithi-navy'}`;
  };

  const handleNavClick = (item, e) => {
    if (!item.hash) return;
    if (location.pathname === '/') {
      e.preventDefault();
      document.querySelector(item.hash)?.scrollIntoView({ behavior: 'smooth' });
      window.history.replaceState(null, '', item.hash);
    } else {
      e.preventDefault();
      navigate({ pathname: '/', hash: item.hash.slice(1) });
    }
  };

  const phoneDisplay = BRAND.contact.phoneDisplay;
  const navItems = homepage ? HOME_NAV : DEFAULT_NAV;

  return (
    <header
      className={`sticky top-0 z-[100] transition-all duration-300 bg-white border-b ${
        scrolled ? 'shadow-sm border-[#F0F0F0]' : 'border-[#F0F0F0]'
      }`}
    >
      <div
        className={`max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-4 ${
          homepage ? 'h-[70px]' : 'py-3'
        }`}
      >
        <BrandLogo />

        {showNav && (
          <nav className={`hidden lg:flex items-center ${homepage ? 'gap-6 absolute left-1/2 -translate-x-1/2' : 'gap-7'}`}>
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.hash && location.pathname === '/' ? item.hash : item.to}
                className={navClass(item)}
                onClick={(e) => handleNavClick(item, e)}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {backTo && (
            <Button
              onClick={() => navigate(backTo)}
              variant="ghost"
              className="text-atithi-navy font-body hidden sm:inline-flex"
            >
              {backLabel}
            </Button>
          )}
          {showNav && (
            <a
              href={`tel:${BRAND.contact.phone}`}
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-[#E8651A] hover:bg-[#C8520F] text-white rounded-lg font-body text-[14px] font-medium transition-colors"
              data-testid="header-call-button"
            >
              <Phone size={18} weight="fill" />
              Call Now: {phoneDisplay}
            </a>
          )}
          {showAdmin && (
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="border-atithi-navy text-atithi-navy hover:bg-atithi-navy hover:text-white font-body"
              data-testid="admin-dashboard-button"
            >
              Admin
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
